import React, { useId, useRef, useState, useEffect } from 'react';
import currency from 'currency.js';
import classnames from 'classnames';

import Show from 'components/Show';

const CURRENCY_REGEX = /^[0-9]+(\.[0-9]{0,2})?$/;

const Currency = ({ name, value, max, placeholder, label, onChange, readOnly, className, inputClassName }) => {
	// Generate random id once
	const id = useId();

	// Use ref for parsed flag
	const parsed = useRef(false);

	// Store local currency
	const [amount, setAmount] = useState('');

	// Container's classnames
	const containerClassName = classnames(
		'flex flex-col',
		className
	);

	// Input's classnames
	const elementClassName = classnames(
		'px-3 h-9 w-full bg-transparent border border-neutral-200 dark:border-neutral-700 dark:text-white text-neutral-800 placeholder:text-neutral-400 rounded-lg focus:border-transparent focus:ring focus:ring-amber-500 read-only:cursor-not-allowed read-only:focus:ring-0 transition-all',
		inputClassName
	);

	// Handle input's value change
	const handle = (event) => {
		// Update parsed flag
		parsed.current = true;
		
		// Get raw input's value
		const raw = event.target.value;

		// Test for allowed characters
		if (raw.length > 0 && !CURRENCY_REGEX.test(raw)) return;

		// Format currency to two decimals
		const formatted = currency(raw, { precision: 2 });

		// Do not allow larger number than max
		if (Number.isInteger(max) && formatted.intValue > max) return;

		// Update local input's value to raw value
		setAmount(raw);

		// Update parent's value to int value
		onChange({
			name,
			value: formatted.intValue
		});
	};

	// Handle input's blur event
	const blur = () => {
		// Format currency to two decimals
		const formatted = currency(amount, { precision: 2 });

		// Update local input's value to two decimals
		setAmount(Number(formatted.value).toFixed(2));
	};

	// Handle input's initialization
	useEffect(() => {
		// Do nothing if already parsed
		if (parsed.current) return;

		// Do not parse no value
		if (!value?.toString()?.length) return;

		// Format passed int value to two decimals
		const formatted = currency(value, { precision: 2, fromCents: true });

		// Update local input's value to two decimals
		setAmount(Number(formatted.value).toFixed(2));

		// Update parsed flag
		parsed.current = true;
	}, [value]);

	return (
		<div className={containerClassName}>
			{/* Show label only if passed */}
			<Show if={label}>
				<label
					htmlFor={id}
					className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors"
				>
					{label}
				</label>
			</Show>

			<input
				id={id}
				type="text"
				name={name}
				value={amount}
				placeholder={placeholder}
				onBlur={blur}
				onChange={handle}
				readOnly={readOnly}
				className={elementClassName}
			/>
		</div>
	);
};

export default Currency;
